/**
 * Saved search presets — localStorage helpers for recents + pinned.
 *
 * Storage key: browse_presets
 * Schema: { pinned: [{ id, name, filters, labels, createdAt }],
 *            recents: [{ id, filters, labels, usedAt }] }
 */

const STORAGE_KEY = 'browse_presets';
const MAX_RECENTS = 10;

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { pinned: [], recents: [] };
  } catch {
    return { pinned: [], recents: [] };
  }
}

function write(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function fingerprint(filters) {
  const entries = Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

function uuid() {
  return crypto.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export function getPresets() {
  return read();
}

export function addRecent(filters, labels) {
  const fp = fingerprint(filters);
  if (fp === '[]') return; // no filters
  const data = read();
  // Remove existing with same fingerprint
  data.recents = data.recents.filter(r => fingerprint(r.filters) !== fp);
  // Also skip if this exact filter set is already pinned
  if (data.pinned.some(p => fingerprint(p.filters) === fp)) return;
  data.recents.unshift({ id: uuid(), filters, labels, usedAt: Date.now() });
  data.recents = data.recents.slice(0, MAX_RECENTS);
  write(data);
}

export function pinRecent(id, name) {
  const data = read();
  const idx = data.recents.findIndex(r => r.id === id);
  if (idx === -1) return;
  const recent = data.recents.splice(idx, 1)[0];
  data.pinned.push({ id: recent.id, name, filters: recent.filters, labels: recent.labels, createdAt: Date.now() });
  write(data);
}

export function pinFilters(filters, labels, name) {
  const data = read();
  const fp = fingerprint(filters);
  // Remove from recents if exists
  data.recents = data.recents.filter(r => fingerprint(r.filters) !== fp);
  // Don't duplicate in pinned
  if (data.pinned.some(p => fingerprint(p.filters) === fp)) return;
  data.pinned.push({ id: uuid(), name, filters, labels, createdAt: Date.now() });
  write(data);
}

export function unpinPreset(id) {
  const data = read();
  data.pinned = data.pinned.filter(p => p.id !== id);
  write(data);
}

export function deleteRecent(id) {
  const data = read();
  data.recents = data.recents.filter(r => r.id !== id);
  write(data);
}

export function clearAllPresets() {
  write({ pinned: [], recents: [] });
}

export function buildPresetLabel(labels) {
  return Object.values(labels).filter(Boolean).join(' \u00b7 ');
}
